import { AppBar, Box, Button, ButtonGroup, Toolbar, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { selectUser, UserState } from "../../../redux/user/userSlice";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../app/hooks";
import { DptoInfo, dptoSlice, retrieveDpto, selectDpto } from "../../../redux/dpto/dptoSlice";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { retrieveSchool, schoolSlice, selectSchool } from "../../../redux/school/schoolSlice";
import { ContextMenu, ContextMenuOptions } from "../../../features/base/ContextMenu";
import DptoPageTitle from "../basics/DptoPageTitle";
import DptoPageAvatar from "../basics/DptoPageAvatar";
import { dptoInsUpdSlice, dptoDelete } from "../../../redux/dpto/dptoInsUpdSlice";
import PageAuthenticatedLoading from "../../../features/pages/PageAuthenticatedLoading";
import { imageInsSlice } from "../../../redux/image/imageInsSlice";
import MenuIcon from "@mui/icons-material/Menu";
import { LoadState } from "../../../redux/common";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { ObjectTarget, PERMISSIONS, PERMISSIONS_TO_ALLOW_CONVERSATION, PermissionId, anyPermission, checkIsAdmin, hasDirectPermission} from "../../../app/permissions";
import { routeDptoEdit, routeDptoImage } from "../../../routes/dpto";
import { routeSchool } from "../../../routes/school";
import DptoUsersList from "./DptoUsersList";
import { usersForPermissionsSlice } from "../../../redux/user/usersForPermissionsSlice";
import { ElementMissingContext } from "../../../features/base/ElementMissingContext";
import { TopBar } from "../../../features/top/TopBar";
import { useEffect, useState } from "react";

import MessageIcon from "@mui/icons-material/Message";
// import PollIcon from '@mui/icons-material/Poll';
import ShieldIcon from '@mui/icons-material/Shield';
import EditIcon from "@mui/icons-material/Edit";

import { DptoConversation } from "../conversation/DptoConversation";
import SurveyReport from "../../../features/survey/SurveyReport";
import TooltipButton from "../../../components/TooltipButton";

const VALID_OPTIONS = ["main", "messages", "survey-report"];

export type DptoContentOptions = typeof VALID_OPTIONS[number];

export function Dpto() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const school = useSelector(selectSchool);
    const dpto = useSelector(selectDpto);
    const admin = checkIsAdmin(user.priza.info);

    const getInitialContentFromHash = (): DptoContentOptions => {
        const hash = location.hash.replace('#', '');
        if (VALID_OPTIONS.includes(hash as DptoContentOptions)) {
            return hash as DptoContentOptions;
        }
        return "main";
    };

    const [contentSelected, setContentSelected] = useState(getInitialContentFromHash());

    const handleTabChange = (option: string) => {
        if (VALID_OPTIONS.includes(option)) {
            setContentSelected(option as DptoContentOptions);
            window.location.hash = option;
        } else {
            setContentSelected("main");
            window.location.hash = "main";
        }
    };

    const { schoolId, dptoId } = useParams();
    // console.log(`SchoolId: ${schoolId}/${school.state.state}, DptoId: ${dptoId}/${dpto.state.state}`);

    useEffect(() => {
        if (school.selected && school.selected.id != schoolId) {
            dispatch(schoolSlice.actions.clean());
        }
        if (school.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveSchool({ schoolId: schoolId }));
        }
    }, [schoolId, school, user]);

    useEffect(() => {
        if (dptoId && dpto.selected && dpto.selected.id != dptoId) {
            dispatch(dptoSlice.actions.clean());
        }
        if (dptoId && dpto.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveDpto({ dptoId: dptoId }));
        }
    }, [dptoId, dpto, user]);

    if ((!schoolId) || (!dptoId)) {
        return <ElementMissingContext />;
    }

    const isAdmin = checkIsAdmin(user.priza.info);

    const cleanup = () => {
        dispatch(schoolSlice.actions.clean());
        dispatch(dptoSlice.actions.clean());
        dispatch(usersForPermissionsSlice.actions.clean());
    };

    const options: ContextMenuOptions[] = [];
    options.push({ label: "Atualizar", click: cleanup });
    if (admin) {
        options.push({
            label: "Editar", click: () => {
                if (dpto.selected) {
                    const newDpto: DptoInfo = { ...dpto.selected };
                    dispatch(dptoInsUpdSlice.actions.set(newDpto));
                    navigate(routeDptoEdit(schoolId, dptoId));
                }
            }
        });
        options.push({
            label: "Alterar Imagem", click: () => {
                if (dpto.selected) {
                    dispatch(imageInsSlice.actions.clean());
                    navigate(routeDptoImage(schoolId, dptoId));
                }
            }
        });
        options.push({
            label: "Remover", click: () => setOpenDeleteDialog(true)
        });
    }
        // Delete dialog state
        const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
        const [deleting, setDeleting] = useState(false);
        const [deleteError, setDeleteError] = useState<string|null>(null);

        const handleDeleteCancel = () => {
            setOpenDeleteDialog(false);
            setDeleteError(null);
        };

        const handleDeleteConfirm = async () => {
            setDeleting(true);
            setDeleteError(null);
            try {
                await dispatch(dptoDelete({ dptoId: dptoId }));
                setOpenDeleteDialog(false);
                navigate(routeSchool(schoolId, "department"));
            } catch (err: any) {
                setDeleteError("Erro ao remover departamento. Tente novamente.");
                setDeleting(false);
            }
        };
    const topMenu = <ContextMenu label={<MenuIcon />} options={options} />;

    const breadcrumbs = [
        {
            label: school.selected?.name ? school.selected?.name : "Escola",
            href: routeSchool(schoolId, "department")
        },
        {
            label: dpto.selected?.name || "Departamento",
        },
    ];

    const canSendMessage = () => {
        for(const permission of PERMISSIONS_TO_ALLOW_CONVERSATION) {
            const perm = PERMISSIONS[permission];
            if (!perm) {
                continue;
            }
            if (perm.target != ObjectTarget.DPTO) {
                continue;
            }
            if (hasDirectPermission(user.priza.info, permission, dptoId)) {
                return true;
            }
        }
        return false;
    };

    return <>
        <Dialog open={openDeleteDialog} onClose={handleDeleteCancel} aria-labelledby="delete-dpto-dialog-title">
            <DialogTitle id="delete-dpto-dialog-title" >Remover Departamento</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Esta ação é <b>irreversível</b> e todos os dados associados serão perdidos!
                </DialogContentText>
                <DialogContentText sx={{ color: 'error.main' }}>
                    Tem certeza que deseja <b>REMOVER</b> o departamento <b>{dpto.selected?.name}</b>?
                </DialogContentText>
                {deleteError && <DialogContentText color="error">{deleteError}</DialogContentText>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDeleteCancel} 
                    color="primary" variant="contained" disabled={deleting}>Cancelar</Button>
                <Button onClick={handleDeleteConfirm} 
                    color="error" variant="contained" disabled={deleting} autoFocus>
                    {deleting ? "Removendo..." : "Remover"}
                </Button>
            </DialogActions>
        </Dialog>
        <PageAuthenticatedLoading
        topbar={<TopBar
            topMenu={topMenu}
            title={
                <Typography variant="h6" component="div">
                    Departamento
                </Typography>
            }
            breadcrumbs={breadcrumbs}
        />}
        loading={{
            states: [school.state, dpto.state],
            content: () => {
                const body = () => {
                    switch (contentSelected) {
                        case "main": return <DptoUsersList schoolId={schoolId} dptoId={dptoId} />;
                        case "messages": return <DptoConversation school={school.selected!} dpto={dpto.selected!} />;
                        case "survey-report": return <SurveyReport target={ObjectTarget.DPTO} targetId={dptoId} />;
                    }
                };

                return <>
                    <AppBar key={"app-name"} id={"app-name"} position="static" color="default">
                        <Toolbar>
                            <DptoPageAvatar />
                            <DptoPageTitle schoolId={schoolId} dptoId={dptoId} />
                            <Box sx={{ flexGrow: 1 }} />
                            <Button onClick={() => navigate(routeSchool(schoolId, "department"))}>Voltar</Button>
                        </Toolbar>
                    </AppBar>
                    <AppBar key={"app-control"} id={"app-control"} position="static" color="default" elevation={2}>
                        <Toolbar>
                            <ButtonGroup variant="contained" color="primary">
                                <TooltipButton
                                    option="main"
                                    optionSelected={contentSelected}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Membros">
                                    <ShieldIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="messages"
                                    optionSelected={contentSelected}
                                    onClick={(option) => handleTabChange(option)}
                                    disabled={!canSendMessage()}
                                    tooltip="Mensagens">
                                    <MessageIcon />
                                </TooltipButton>
                                {/* <TooltipButton
                                    option="survey-report"
                                    optionSelected={contentSelected}
                                    disabled={!canSendMessage}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Resultado de enquetes">
                                    <PollIcon />
                                </TooltipButton> */}
                                <TooltipButton
                                    option="edit-dpto"
                                    optionSelected={contentSelected}
                                    disabled={!canEditDpto(user, schoolId)}
                                    onClick={() => {
                                            const newDpto = { ...dpto.selected } as DptoInfo;
                                            dispatch(dptoInsUpdSlice.actions.set(newDpto));
                                            navigate(routeDptoEdit(schoolId, dptoId));
                                        }
                                    }
                                    tooltip="Editar Sala">
                                    <EditIcon />
                                </TooltipButton>
                            </ButtonGroup>
                        </Toolbar>
                    </AppBar>
                    {body()}
                </>;
            },
            error: "Erro ao carregar lista de departamentos",
            retry: cleanup
        }} />;
    </>;
}

export function canEditDpto(user: UserState, schoolId?: string) {
    return anyPermission(user.priza.info, schoolId, [PermissionId.SCHOOL_ADMIN, PermissionId.DPTO_ADMIN]);
}
